// Sidebar activity polling: per-tab counts (actionable items) + latest-change timestamps.
import { request } from './client.js';

export interface TabActivity {
  count: number;
  latest: string | null;
}

export type ActivityMap = Record<string, TabActivity>;

// The server's clock from the most recent poll. lastSeen must be stamped against the same
// clock that stamps created_at in the database — a browser clock running behind the server
// would otherwise leave `latest > lastSeen` true forever and the dot could never clear.
let serverNow: string | null = null;
const nowIso = () => serverNow ?? new Date().toISOString();

export async function fetchActivity(engagementId: number): Promise<ActivityMap> {
  const res = await request<{ engagement_id: number; tabs: ActivityMap; now?: string }>(
    `/activity?engagement_id=${engagementId}`
  );
  if (res.now) serverNow = res.now;
  return res.tabs;
}

// ---- lastSeen bookkeeping (localStorage) ----------------------------------
// Key: signet.lastSeen.<engagementId>.<tab> → ISO timestamp of when the operator
// last had that tab open. The dot shows only when latest > lastSeen.

const seenKey = (engagementId: number, tab: string) => `signet.lastSeen.${engagementId}.${tab}`;

export function getLastSeen(engagementId: number, tab: string): string | null {
  return localStorage.getItem(seenKey(engagementId, tab));
}

export function markSeen(engagementId: number, tab: string): void {
  localStorage.setItem(seenKey(engagementId, tab), nowIso());
}

/**
 * First contact with an engagement (no lastSeen keys yet): baseline every tab to "now"
 * so historical activity does not light up the whole sidebar. Dots then only appear
 * for changes that happen after the operator started using the feature.
 */
export function baselineIfFresh(engagementId: number, tabs: string[]): void {
  if (tabs.some((t) => localStorage.getItem(seenKey(engagementId, t)) !== null)) return;
  const now = nowIso();
  for (const t of tabs) localStorage.setItem(seenKey(engagementId, t), now);
}
